import type { BillingStatus } from "../components/BillingStatusContext";
import { hasOrgAccess } from "../components/BillingStatusContext";

export type CurrentPage =
  | "home"
  | "results"
  | "gallery"
  | "refine"
  | "pricing"
  | "admin"
  | "organization"
  | "source";

export interface NavItem {
  to: "/" | "/source" | "/gallery" | "/admin" | "/organization";
  label: string;
  isActive: boolean;
}

export function buildNavItems(
  currentPage: CurrentPage,
  billingStatus: BillingStatus | null,
  isBillingLoading: boolean,
  showSourceLink = false,
): NavItem[] {
  const isGenerator =
    currentPage === "home" ||
    currentPage === "results" ||
    currentPage === "refine";
  const isGallery = currentPage === "gallery";
  const isAdmin = currentPage === "admin";
  const isOrganization = currentPage === "organization";
  const showAdminLink = billingStatus?.is_admin ?? false;
  const showOrganizationLink = !isBillingLoading && hasOrgAccess(billingStatus);

  return [
    // Create and Gallery are always present. A user who has spent the month's
    // allowance still belongs on the design tab — the out-of-iterations
    // overlay lives there, and hiding the tab would leave them nowhere to see
    // it. Pricing is deliberately absent: the IterationCounter pill by the
    // logo is the upgrade path for free users.
    { to: "/" as const, label: "Create", isActive: isGenerator },
    // Source is staging-only for now (`useSourceTabEnabled`).
    ...(showSourceLink
      ? [
          {
            to: "/source" as const,
            label: "Source",
            isActive: currentPage === "source",
          },
        ]
      : []),
    {
      to: "/gallery" as const,
      label: "Gallery",
      isActive: isGallery,
    },
    ...(showOrganizationLink
      ? [
          {
            to: "/organization" as const,
            label: "Organization",
            isActive: isOrganization,
          },
        ]
      : []),
    ...(showAdminLink
      ? [{ to: "/admin" as const, label: "Admin", isActive: isAdmin }]
      : []),
  ];
}
